import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

import API_URL from '../../config';
const API_BASE = `${API_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [alert, setAlert] = useState({ id: '', type: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('projabze_theme') || 'light');

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('projabze_theme', next);
  };

  // Form states
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPasswordConfirm, setRegPasswordConfirm] = useState('');
  const [showLoginPw, setShowLoginPw] = useState(false);
  const [showRegPw, setShowRegPw] = useState(false);
  const [showRegPwConfirm, setShowRegPwConfirm] = useState(false);

  // Yasal metin onayları (kayıt sırasında) — KVKK/Sözleşme zorunlu, diğerleri opsiyonel
  const [consentRequired, setConsentRequired] = useState(false); // tek checkbox: tos+kvkk
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [consentProfile, setConsentProfile] = useState(false);
  const [consentTestimonial, setConsentTestimonial] = useState(false);

  // Contact form
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactAlert, setContactAlert] = useState({ type: '', message: '' });
  const [contactLoading, setContactLoading] = useState(false);

  // Public stats — backend'den agrega değerler
  const [stats, setStats] = useState({ projectCount: 0, userCount: 0, timeSaving: 80, uptime: '24/7' });
  useEffect(() => {
    fetch(`${API_BASE}/public/stats`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setStats(d); })
      .catch(() => { /* sessiz */ });
  }, []);

  // Testimonials — admin onayli + acik rizali kullanici yorumlari
  const [testimonials, setTestimonials] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE}/public/testimonials`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.testimonials) setTestimonials(d.testimonials); })
      .catch(() => {});
  }, []);

  // Pricing tiers — admin panelden yayinlanan tier'lar
  const [pricingTiers, setPricingTiers] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE}/pricing`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d?.tiers) return;
        // Slug'a göre dedupe et (admin tarafında çoğaltılmış olabilir)
        const seen = new Set();
        const uniq = [];
        for (const t of d.tiers) {
          const k = t.slug || t.id;
          if (seen.has(k)) continue;
          seen.add(k);
          uniq.push(t);
        }
        // monthlyPrice'a göre sırala (artan)
        uniq.sort((a, b) => (a.monthlyPrice || 0) - (b.monthlyPrice || 0));
        setPricingTiers(uniq);
      })
      .catch(() => { /* sessiz — fallback statik kart */ });
  }, []);

  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          observer.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Counter animation — stats fetch tamamlandıktan sonra tekrar başlar
  useEffect(() => {
    const counterObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const target = parseInt(el.dataset.count || el.textContent);
        if (Number.isNaN(target) || target <= 0) {
          el.textContent = '0' + (el.dataset.suffix || '');
          counterObserver.unobserve(el);
          return;
        }
        const suffix = el.dataset.suffix || (el.dataset.count ? '+' : '');
        let current = 0;
        const step = Math.max(1, Math.ceil(target / 60));
        const timer = setInterval(() => {
          current = Math.min(current + step, target);
          el.textContent = current + suffix;
          if (current >= target) clearInterval(timer);
        }, 20);
        counterObserver.unobserve(el);
      });
    }, { threshold: 0.5 });
    document.querySelectorAll('[data-count]').forEach(el => counterObserver.observe(el));
    return () => counterObserver.disconnect();
  }, [stats]);

  // ESC key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const openModal = useCallback((tab = 'login') => {
    setModalOpen(true);
    setActiveTab(tab);
    setAlert({ id: '', type: '', message: '' });
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    document.body.style.overflow = '';
    setAlert({ id: '', type: '', message: '' });
  }, []);

  const showAlert = (id, type, message) => {
    setAlert({ id, type, message });
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Login handler
  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) {
      return showAlert('login', 'error', 'Lütfen tüm alanları doldurun.');
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('projabze_token', data.token);
        localStorage.setItem('projabze_user', JSON.stringify(data.user));
        showAlert('login', 'success', 'Giriş başarılı! Yönlendiriliyorsunuz...');
        setTimeout(() => {
          closeModal();
          navigate('/dashboard');
        }, 1500);
      } else {
        showAlert('login', 'error', data.message || 'E-posta veya şifre hatalı.');
      }
    } catch (err) {
      showAlert('login', 'error', 'Sunucuya ulaşılamadı. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  // Register handler
  const handleRegister = async () => {
    if (!regFirstName || !regLastName || !regEmail || !regPassword) {
      return showAlert('register', 'error', 'Lütfen tüm alanları doldurun.');
    }
    if (regPassword.length < 8) {
      return showAlert('register', 'error', 'Şifre en az 8 karakter olmalıdır.');
    }
    if (regPassword !== regPasswordConfirm) {
      return showAlert('register', 'error', 'Şifreler eşleşmiyor.');
    }
    if (!consentRequired) {
      return showAlert('register', 'error', 'Üyelik Sözleşmesi ve KVKK Aydınlatma Metni\'ni kabul etmelisiniz.');
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: regFirstName,
          lastName: regLastName,
          email: regEmail,
          password: regPassword,
          consents: {
            tos: consentRequired,
            kvkk: consentRequired,
            marketing: consentMarketing,
            profileVisibility: consentProfile,
            testimonial: consentTestimonial,
          },
        })
      });
      const data = await res.json();
      if (res.ok) {
        showAlert('register', 'success', 'Hesabınız oluşturuldu! Giriş yapabilirsiniz.');
        setTimeout(() => setActiveTab('login'), 2500);
      } else {
        showAlert('register', 'error', data.message || 'Kayıt sırasında hata oluştu.');
      }
    } catch (err) {
      showAlert('register', 'error', 'Sunucuya ulaşılamadı. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  // Forgot password
  const handleForgotPassword = () => {
    if (!loginEmail) return showAlert('login', 'error', 'Önce e-posta adresinizi girin.');
    showAlert('login', 'success', 'Şifre sıfırlama bağlantısı e-postanıza gönderildi.');
  };

  // Contact form
  const handleContact = async (e) => {
    e.preventDefault();
    if (!contactName || !contactEmail || !contactMessage) {
      return setContactAlert({ type: 'error', message: 'Ad, e-posta ve mesaj zorunludur.' });
    }
    setContactLoading(true);
    try {
      const res = await fetch(`${API_BASE}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: contactName,
          email: contactEmail,
          subject: contactSubject,
          message: contactMessage
        })
      });
      if (res.ok) {
        setContactAlert({ type: 'success', message: 'Mesajınız iletildi. En kısa sürede dönüş yapacağız.' });
        setContactName(''); setContactEmail(''); setContactSubject(''); setContactMessage('');
      } else {
        throw new Error();
      }
    } catch {
      setContactAlert({ type: 'error', message: 'Gönderim sırasında hata oluştu. Lütfen e-posta ile ulaşın.' });
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div className={`landing-page ${theme}`}>
      {/* NAV */}
      <nav className="landing-nav">
        <div className="nav-logo">Pro<span>jabze</span><span className="beta-badge">beta</span></div>
        <div className="nav-links">
          <a onClick={() => scrollToSection('ozellikler')}>Özellikler</a>
          <a onClick={() => scrollToSection('nasil-calisir')}>Nasıl Çalışır?</a>
          <a onClick={() => scrollToSection('fiyatlandirma')}>Fiyatlandırma</a>
          <a onClick={() => scrollToSection('iletisim')}>İletişim</a>
        </div>
        <div className="nav-cta">
          <button className="theme-toggle" onClick={toggleTheme} title={theme === 'dark' ? 'Açık Mod' : 'Karanlık Mod'}>
            {theme === 'dark' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
            )}
          </button>
          <button className="btn-ghost" onClick={() => openModal('login')}>Giriş Yap</button>
          <button className="btn-primary" onClick={() => openModal('register')}>Ücretsiz Başla</button>
        </div>
      </nav>

      {/* HERO */}
      <section className="hero">
        <div className="hero-glow"></div>
        <div className="hero-glow2"></div>
        <div className="badge"><span className="badge-dot"></span> Türkiye'nin ilk bulut tabanlı elektrik CAD platformu</div>
        <h1>
          <span className="hero-question">Elektrik projesi hâlâ bir gününüzü mü alıyor?</span>
          <span className="hero-answer">Sabah çizin, öğlen teslim edin.</span>
        </h1>
        <p className="hero-sub">Kablo çekilir çekilmez yük, akım ve gerilim düşümü otomatik. Yönetmelik sınırı aşıldığında sistem sizi uyarır. Elektrik projesinin yapılış şekli değişiyor.</p>
        <div className="hero-actions">
          <button className="btn-large demo-glow" onClick={() => window.open('/app', 'projabze-editor')}>60 saniyelik demo</button>
          <button className="btn-large primary" onClick={() => openModal('register')}>Ücretsiz Başla</button>
          <button className="btn-large outline" onClick={() => scrollToSection('nasil-calisir')}>Nasıl Çalışır? &rarr;</button>
        </div>
        {/* App Preview — gerçek uygulama ekran görüntüsü */}
        <div className="hero-mock hero-mock-image">
          <img
            src="/landing-app-preview.png"
            alt="Projabze uygulama önizlemesi"
            className="hero-preview-img"
          />
        </div>
      </section>

      {/* STATS */}
      <div className="stats">
        <div className="stat reveal">
          <div className="stat-num" data-count={stats.projectCount}>0</div>
          <div className="stat-label">Çizilen Proje</div>
        </div>
        <div className="stat reveal">
          <div className="stat-num" data-count={stats.userCount}>0</div>
          <div className="stat-label">Aktif Mühendis</div>
        </div>
        <div className="stat reveal">
          <div className="stat-num" data-suffix="%">{stats.timeSaving}</div>
          <div className="stat-label">Zaman Tasarrufu</div>
        </div>
        <div className="stat reveal">
          <div className="stat-num">{stats.uptime}</div>
          <div className="stat-label">Erişim</div>
        </div>
      </div>

      {/* FEATURES */}
      <section className="features" id="ozellikler">
        <div className="features-header reveal">
          <div className="section-tag">Özellikler</div>
          <h2>Her İhtiyacınız İçin<br/>Güçlü Araçlar</h2>
          <p className="section-lead">Elektrik proje sürecinizi baştan sona otomatize eden, profesyonel standartlarda araçlar.</p>
        </div>
        <div className="features-grid">
          {[
            { img: '/features/otomatik-hesaplamalar.jpg',    title: 'Otomatik Hesaplamalar',    desc: 'Kesit ve kesici atamaları önceden belirlenen şablonlara göre otomatik yapılır. Hata riski minimuma iner.' },
            { img: '/features/mimari-uyumlu-cizim.jpg',      title: 'Mimari Uyumlu Çizim',      desc: 'Onaylı mimari kat planlarının üzerine doğrudan elektrik projenizi çizin. Tam uyumluluk garantisi.' },
            { img: '/features/bulut-tabanli-paylasim.jpg',   title: 'Bulut Tabanlı Paylaşım',   desc: 'Proje müellifleri, onay makamları ve yükleniciler aynı proje üzerinde eş zamanlı çalışabilir.' },
            { img: '/features/yonetmelik-sablonlari.jpg',    title: 'Yönetmelik Şablonları',    desc: 'Farklı kurumların farklı mevzuatlarına uygun şablonları önceden hazırlayın, tek tıkla uygulayın.' },
            { img: '/features/revizyon-onay-akisi.jpg',      title: 'Revizyon & Onay Akışı',    desc: 'Geri bildirimler anında iletilir, revizyonlar dijital ortamda takip edilir, onay süreci hızlanır.' },
            { img: '/features/otomatik-malzeme-listesi.jpg', title: 'Otomatik Malzeme Listesi', desc: 'Çizim tamamlanırken malzeme metrajları ve kesit bilgileri otomatik olarak güncellenir.' },
          ].map((f, i) => (
            <div className="feature-card reveal" key={i}>
              <div className="feature-image">
                <img src={f.img} alt={f.title} loading="lazy" />
              </div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AUDIENCE */}
      <section id="kullanici-kitlesi">
        <div className="reveal">
          <div className="section-tag">Kimler Kullanır?</div>
          <h2>Her Paydaş İçin<br/>Tasarlandı</h2>
          <p className="section-lead">Projabze, elektrik proje sürecindeki tüm aktörlerin ihtiyaçlarını karşılar.</p>
        </div>
        <div className="audience-grid">
          {[
            { icon: '\uD83D\uDC77', title: 'Elektrik Proje Müellifleri', desc: 'Projeleri hızlı çizin, otomatik hesaplamalar yapın ve ruhsat süreçlerini kolaylaştırın.' },
            { icon: '\uD83C\uDFE2', title: 'Mühendislik Büroları', desc: 'Ekip içi işbirliği, proje arşivleme ve standartlaşma ile verimliliği artırın.' },
            { icon: '\uD83C\uDFDB\uFE0F', title: 'Onay & Denetleme Makamları', desc: 'Projeleri online inceleyin, notlar ekleyin ve onay süreçlerini dijital ortamda yönetin.' },
          ].map((a, i) => (
            <div className="audience-card reveal" key={i}>
              <div className="audience-icon">{a.icon}</div>
              <h3>{a.title}</h3>
              <p>{a.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="how" id="nasil-calisir">
        <div style={{textAlign:'center'}} className="reveal">
          <div className="section-tag">Süreç</div>
          <h2>5 Adımda Proje Hazır</h2>
          <p className="section-lead" style={{margin:'0 auto'}}>Uygulamamız, her aşamada size rehberlik eder ve süreci kolaylaştırır.</p>
        </div>
        <div className="steps">
          {[
            { num: '01', title: 'Belge Yükle', desc: 'Onaylı mimari proje ve belgelerle başlayın' },
            { num: '02', title: 'Proje Çiz', desc: 'Otomatik hesaplamalarla hızlı ve doğru çizim' },
            { num: '03', title: 'Online Gönder', desc: 'Kurum ve kişilerle bulut üzerinden paylaş' },
            { num: '04', title: 'Revize Et', desc: 'Geri bildirimlere göre hızlıca güncelle ve onay al' },
            { num: '05', title: 'Uygula', desc: 'Onaylı projenizle saha uygulamasına başla' },
          ].map((s, i) => (
            <div className="step reveal" key={i}>
              <div className="step-num">{s.num}</div>
              <h4>{s.title}</h4>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="fiyatlandirma">
        <div className="pricing-header reveal">
          <div className="section-tag">Fiyatlandırma</div>
          <h2>İhtiyacınıza Uygun Plan</h2>
          <p className="section-lead">Tüm planlar ücretsiz deneme süresiyle başlar. Kredi kartı gerekmez.</p>
        </div>
        <div className="pricing-grid">
          {(pricingTiers && pricingTiers.length > 0) ? (() => {
            // En popüler: en yüksek seat sayısı dışında orta tier (price > 0, en pahalı değil)
            // Pratik kural: 2. en pahalı tier popüler kabul edilir; yoksa ortadaki
            const paid = pricingTiers.filter(t => (t.monthlyPrice || 0) > 0);
            const popularId = paid.length >= 2 ? paid[Math.min(paid.length - 2, 1)].id : null;
            return pricingTiers.map(t => {
              const isFree = (t.monthlyPrice || 0) === 0;
              const isLast = pricingTiers[pricingTiers.length - 1]?.id === t.id;
              const isPopular = t.id === popularId;
              const priceFmt = (n) => new Intl.NumberFormat('tr-TR').format(n);
              return (
                <div key={t.id} className={`pricing-card reveal${isPopular ? ' popular' : ''}`}>
                  {isPopular && <div className="popular-badge">En Popüler</div>}
                  <div className="pricing-name">{t.name}</div>
                  <div className="pricing-price">
                    {isFree
                      ? 'Ücretsiz'
                      : (<><sup>&#8378;</sup>{priceFmt(t.monthlyPrice)}<sub>/ay</sub></>)}
                  </div>
                  {t.maxSeats && (
                    <p className="pricing-desc">
                      {t.maxSeats === 1
                        ? 'Bireysel kullanım için.'
                        : `${t.maxSeats} kullanıcıya kadar koltuk.`}
                    </p>
                  )}
                  <ul className="pricing-features">
                    {(t.features || []).map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                  <button
                    className={`btn-pricing ${isPopular ? 'filled' : 'outline'}`}
                    onClick={() => isLast ? scrollToSection('iletisim') : openModal('register')}
                  >
                    {isFree ? 'Ücretsiz Başla' : (isLast ? 'İletişime Geç' : 'Hemen Başla')}
                  </button>
                </div>
              );
            });
          })() : (
            <div className="pricing-loading reveal" style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--muted)', padding: '2rem' }}>
              Fiyatlandırma yükleniyor...
            </div>
          )}
        </div>
      </section>

      {/* PROOF */}
      <section className="proof">
        <div className="reveal" style={{textAlign:'center',marginBottom:0}}>
          <div className="section-tag">Referanslar</div>
          <h2>Mühendisler Ne Diyor?</h2>
        </div>
        <div className="proof-grid">
          {((testimonials && testimonials.length > 0) ? testimonials.map(t => {
            const initials = (t.authorName || "?").split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
            const filled = Math.max(1, Math.min(5, t.stars || 5));
            return {
              stars: '★'.repeat(filled) + '☆'.repeat(5 - filled),
              text: '"' + t.text + '"',
              avatar: initials,
              avatarUrl: t.authorAvatar,
              name: t.authorName,
              role: [t.authorRole, t.authorCity].filter(Boolean).join(' - '),
            };
          }) : [
            { stars: '★★★★★', text: '"Projabze ile elektrik projelerini dakikalar içinde hazırlayabilmek harika."', avatar: 'PR', name: 'Projabze Ekibi', role: 'Demo' },
          ]).map((p, i) => (
            <div className="proof-card reveal" key={i}>
              <div className="proof-stars">{p.stars}</div>
              <p className="proof-text">{p.text}</p>
              <div className="proof-author">
                <div className="proof-avatar">
                  {p.avatarUrl
                    ? <img src={p.avatarUrl.startsWith('http') ? p.avatarUrl : `${API_URL}${p.avatarUrl}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                    : p.avatar}
                </div>
                <div><div className="proof-name">{p.name}</div><div className="proof-role">{p.role}</div></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA BANNER */}
      <div className="cta-banner reveal">
        <div className="section-tag">Başlamaya Hazır mısınız?</div>
        <h2>İlk Projenizi Bugün Oluşturun</h2>
        <p>Ücretsiz demo hesabıyla tüm özellikleri deneyin. Kredi kartı gerekmez, kurulum yok.</p>
        <button className="btn-large primary" onClick={() => openModal('register')}>Ücretsiz Demo Başlat &rarr;</button>
      </div>

      {/* CONTACT */}
      <section id="iletisim">
        <div className="contact-grid">
          <div className="contact-info reveal">
            <div className="section-tag">İletişim</div>
            <h2>Sizin İçin<br/>Buradayız</h2>
            <p className="section-lead" style={{marginBottom:'2rem'}}>Sorularınız, demo talepleriniz veya kurumsal çözümler için bizimle iletişime geçin.</p>
            <div className="contact-detail">
              <span className="contact-detail-icon">&#x1F4E7;</span>
              <span>info@projabze.com</span>
            </div>
            <div className="contact-detail">
              <span className="contact-detail-icon">&#x1F310;</span>
              <span>app.projabze.com</span>
            </div>
            <div className="contact-detail">
              <span className="contact-detail-icon">&#x23F0;</span>
              <span>Pazartesi - Cuma: 09:00 - 18:00</span>
            </div>
          </div>
          <div className="reveal">
            <form className="contact-form" onSubmit={handleContact}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem'}}>
                <div className="form-group">
                  <label>Adınız</label>
                  <input type="text" placeholder="Ad Soyad" value={contactName} onChange={e => setContactName(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>E-posta</label>
                  <input type="email" placeholder="örnek@mail.com" value={contactEmail} onChange={e => setContactEmail(e.target.value)} required />
                </div>
              </div>
              <div className="form-group">
                <label>Konu</label>
                <select value={contactSubject} onChange={e => setContactSubject(e.target.value)}>
                  <option value="">Konu Seçin</option>
                  <option value="demo">Demo Talebi</option>
                  <option value="kurumsal">Kurumsal Plan</option>
                  <option value="teknik">Teknik Destek</option>
                  <option value="diger">Diğer</option>
                </select>
              </div>
              <div className="form-group">
                <label>Mesajınız</label>
                <textarea placeholder="Nasıl yardımcı olabiliriz?" value={contactMessage} onChange={e => setContactMessage(e.target.value)} required></textarea>
              </div>
              <button type="submit" className="btn-submit" disabled={contactLoading}>
                {contactLoading ? 'Gönderiliyor...' : 'Mesaj Gönder'}
              </button>
              {contactAlert.message && (
                <div className={`modal-alert ${contactAlert.type}`}>{contactAlert.message}</div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="landing-footer">
        <div className="footer-grid">
          <div className="footer-brand">
            <div className="nav-logo" style={{fontSize:'1.3rem'}}>Pro<span>jabze</span><span className="beta-badge">beta</span></div>
            <p>Elektrik tesisat projelerinizi otomatik hesaplamalar ve bulut tabanlı işbirliği ile dakikalar içinde hazırlayan yeni nesil platform.</p>
          </div>
          <div className="footer-col">
            <h4>Platform</h4>
            <a onClick={() => scrollToSection('ozellikler')}>Özellikler</a>
            <a onClick={() => scrollToSection('fiyatlandirma')}>Fiyatlandırma</a>
            <a onClick={() => scrollToSection('nasil-calisir')}>Nasıl Çalışır?</a>
            <a onClick={() => window.open('/app', 'projabze-editor')}>Uygulamaya Git</a>
          </div>
          <div className="footer-col">
            <h4>Şirket</h4>
            <a onClick={() => scrollToSection('iletisim')}>İletişim</a>
            <a href="#">Hakkımızda</a>
            <a href="#">Blog</a>
            <a href="#">Kariyer</a>
          </div>
          <div className="footer-col">
            <h4>Yasal</h4>
            <a href="/privacy?tab=kvkk">KVKK Aydınlatma</a>
            <a href="/privacy?tab=riza">Açık Rıza Metni</a>
            <a href="/privacy?tab=sozlesme">Üyelik Sözleşmesi</a>
            <a href="/privacy?tab=cerez">Çerez Politikası</a>
            <a href="/privacy?tab=beta">Beta Şartları</a>
          </div>
        </div>
        <div className="footer-bottom">
          <span>&copy; 2025 Projabze. Tüm hakları saklıdır.</span>
          <span>Türkiye'de üretildi &#127481;&#127479;</span>
        </div>
      </footer>

      {/* AUTH MODAL */}
      <div className={`modal-overlay ${modalOpen ? 'active' : ''}`} onClick={(e) => { if (e.target.classList.contains('modal-overlay')) closeModal(); }}>
        <div className="landing-modal">
          <button className="modal-close" onClick={closeModal}>&times;</button>
          <div className="modal-logo">Pro<span>jabze</span><span className="beta-badge">beta</span></div>
          <p className="modal-sub">{activeTab === 'login' ? 'Hesabınıza giriş yapın' : '7 günlük ücretsiz deneme başlat'}</p>
          <div className="modal-tabs">
            <button className={`modal-tab ${activeTab === 'login' ? 'active' : ''}`} onClick={() => { setActiveTab('login'); setAlert({}); }}>Giriş Yap</button>
            <button className={`modal-tab ${activeTab === 'register' ? 'active' : ''}`} onClick={() => { setActiveTab('register'); setAlert({}); }}>Kayıt Ol</button>
          </div>

          {activeTab === 'login' ? (
            <div className="modal-form">
              <div className="form-group">
                <label>E-posta</label>
                <input type="email" placeholder="örnek@mail.com" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Şifre</label>
                <div className="pw-wrapper">
                  <input type={showLoginPw ? 'text' : 'password'} placeholder="********" value={loginPassword} onChange={e => setLoginPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleLogin()} />
                  <button type="button" className="pw-toggle" onClick={() => setShowLoginPw(!showLoginPw)}>
                    {showLoginPw ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    )}
                  </button>
                </div>
              </div>
              <div className="forgot-link" onClick={handleForgotPassword}>Şifremi Unuttum</div>
              <button className="modal-submit" onClick={handleLogin} disabled={loading}>
                {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
              </button>
              {alert.id === 'login' && alert.message && (
                <div className={`modal-alert ${alert.type}`}>{alert.message}</div>
              )}
            </div>
          ) : (
            <div className="modal-form">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'.75rem'}}>
                <div className="form-group">
                  <label>Ad</label>
                  <input type="text" placeholder="Adınız" value={regFirstName} onChange={e => setRegFirstName(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Soyad</label>
                  <input type="text" placeholder="Soyadınız" value={regLastName} onChange={e => setRegLastName(e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label>E-posta</label>
                <input type="email" placeholder="örnek@mail.com" value={regEmail} onChange={e => setRegEmail(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Şifre</label>
                <div className="pw-wrapper">
                  <input type={showRegPw ? 'text' : 'password'} placeholder="En az 8 karakter" value={regPassword} onChange={e => setRegPassword(e.target.value)} />
                  <button type="button" className="pw-toggle" onClick={() => setShowRegPw(!showRegPw)}>
                    {showRegPw ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    )}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Şifre Tekrar</label>
                <div className="pw-wrapper">
                  <input type={showRegPwConfirm ? 'text' : 'password'} placeholder="Şifreyi tekrar girin" value={regPasswordConfirm} onChange={e => setRegPasswordConfirm(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleRegister()} />
                  <button type="button" className="pw-toggle" onClick={() => setShowRegPwConfirm(!showRegPwConfirm)}>
                    {showRegPwConfirm ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    )}
                  </button>
                </div>
              </div>
              {/* Yasal onaylar */}
              <div className="form-consents">
                <label className="consent-line consent-required">
                  <input type="checkbox" checked={consentRequired} onChange={e => setConsentRequired(e.target.checked)} />
                  <span>
                    <strong>(Zorunlu)</strong>{' '}
                    <a href="/privacy?tab=sozlesme" target="_blank" rel="noopener noreferrer">Üyelik Sözleşmesi</a>'ni ve{' '}
                    <a href="/privacy?tab=kvkk" target="_blank" rel="noopener noreferrer">KVKK Aydınlatma Metni</a>'ni okudum, kabul ediyorum.
                  </span>
                </label>
                <div className="consent-optional-block">
                  <div className="consent-optional-title">Açık Rıza (Opsiyonel — istediğiniz zaman geri alabilirsiniz):</div>
                  <label className="consent-line">
                    <input type="checkbox" checked={consentMarketing} onChange={e => setConsentMarketing(e.target.checked)} />
                    <span>Yeni özellikler ve kampanyalar hakkında <strong>e-posta/SMS bildirim</strong> almak istiyorum.</span>
                  </label>
                  <label className="consent-line">
                    <input type="checkbox" checked={consentProfile} onChange={e => setConsentProfile(e.target.checked)} />
                    <span>Profil bilgilerimin diğer kullanıcılara <strong>görünür kılınmasını</strong> kabul ediyorum (DM, takip, yorum için gerekli).</span>
                  </label>
                  <label className="consent-line">
                    <input type="checkbox" checked={consentTestimonial} onChange={e => setConsentTestimonial(e.target.checked)} />
                    <span>İleride yazacağım <strong>olumlu yorumların pazarlama materyallerinde</strong> ad/rol bilgimle yayınlanmasını kabul ediyorum (<a href="/privacy?tab=riza#testimonial" target="_blank" rel="noopener noreferrer">detay</a>).</span>
                  </label>
                </div>
              </div>
              <button className="modal-submit" onClick={handleRegister} disabled={loading}>
                {loading ? 'Hesap oluşturuluyor...' : 'Hesap Oluştur'}
              </button>
              {alert.id === 'register' && alert.message && (
                <div className={`modal-alert ${alert.type}`}>{alert.message}</div>
              )}
              <p style={{fontSize:'.75rem',color:'var(--muted)',textAlign:'center',marginTop:'.5rem'}}>
                Kayıt olarak <a href="#" style={{color:'var(--accent)'}}>Kullanım Şartlarını</a> kabul etmiş olursunuz.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
